import { useState, useCallback, useEffect } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Parallella estimat ("spår") på samma lead.
 *
 * Ett lead kan ha flera estimat som skickas samtidigt — t.ex. "Ovanplan" och
 * "Bottenplan" — där varje spår har sin EGEN versionskedja. Ovanplan kan bli
 * 1.1 medan Bottenplan ligger kvar på 1.0.
 *
 * Leads utan parallella estimat får en lista med exakt ett element, så vyn kan
 * behandla alla leads likadant. Växlaren döljs när det bara finns ett spår.
 */
const useEstimatVariants = (leadId) => {
  const [varianter, setVarianter] = useState([]);
  const [laddar, setLaddar] = useState(false);
  const [dupliceringPagar, setDupliceringPagar] = useState(false);

  const hamtaVarianter = useCallback(async () => {
    if (!leadId) return [];
    setLaddar(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/lead/${leadId}/variants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const lista = data.varianter || [];
      setVarianter(lista);
      return lista;
    } catch (err) {
      console.error('Kunde inte hämta estimatspår:', err);
      // Ett tomt spårlista är inte ett fel som ska blockera vyn — estimatet
      // laddas ändå via den vanliga vägen.
      setVarianter([]);
      return [];
    } finally {
      setLaddar(false);
    }
  }, [leadId]);

  useEffect(() => {
    hamtaVarianter();
  }, [hamtaVarianter]);

  /**
   * Duplicerar ett estimat till ett nytt spår och returnerar det nya estimatet.
   * Kastar vid fel så anroparen kan visa meddelandet — namnkrockar och tomma
   * namn avvisas av backend.
   */
  const duplicera = useCallback(async (estimatId, variantName) => {
    setDupliceringPagar(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/${estimatId}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ variantName })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Kunde inte duplicera estimatet');
      }
      await hamtaVarianter();
      return data;
    } finally {
      setDupliceringPagar(false);
    }
  }, [hamtaVarianter]);

  return {
    varianter,
    laddar,
    dupliceringPagar,
    hamtaVarianter,
    duplicera,
    // Växlaren är meningslös vid ett enda spår — låt vyn dölja den.
    harFleraSpar: varianter.length > 1
  };
};

export default useEstimatVariants;
